import { useCallback, useEffect, useRef } from "react";
import {
  usePolyPitchDetector,
  type PolyNoteEvent,
} from "./usePolyPitchDetector";
import {
  useMonoPitchDetector,
  type MonoDetectedNote,
  type SubscribeOptions,
} from "./useMonoPitchDetector";
import { type NoteName } from "../lib/guitarEngine";
import { matchGuidedPolyphony, type GuidedPolyMatchResult } from "../lib/guidedPolyMatch";

export interface DetectedNote {
  note: NoteName;
  octave: number;
  pitch: number; // MIDI
  confidence: number;
  frequency: number; // Hz (nuevo, opcional para consumidores actualizados)
  cents: number;     // -50…+50 desviación
}

export interface DetectedChord {
  notes: PolyNoteEvent[];
  pitchClasses: number[];
}

export interface AudioEngineState {
  isReady: boolean;
  isLoading: boolean;
  isAnalyzing: boolean;
  error: string | null;
  volume: number;
}

export interface DetectNoteAdvancedOptions {
  /** MIDI esperado: filtra ruido y valida la nota del pentagrama. */
  preferMidi?: number;
  /** Acepta ±1 octava solo si YIN eligió mal la octava (default false). */
  acceptOctaveErrors?: boolean;
  /** Acepta ±1 semitono al comparar contra preferMidi (default false). */
  acceptSemitoneErrors?: boolean;
  /** Tiempo mínimo de sostenimiento (default 150 ms). */
  holdTimeMs?: number;
  /** Confianza mínima (default 0.5). */
  minConfidence?: number;
  /** Silencio previo obligatorio antes de contar el hold (default 0). */
  requireSilenceBeforeMs?: number;
  /** Desviación máxima en cents (default 45). */
  maxCents?: number;
}

/**
 * Hook maestro de audio.
 *
 * detectNote() — Monofónico, motor YIN (rápido, baja latencia, robusto
 *               para nota individual de guitarra/voz).
 * detectChord() — Polifónico, modelo Basic Pitch (ML, ideal para acordes).
 *
 * El motor monofónico mantiene el micrófono ABIERTO entre llamadas, lo
 * que reduce drásticamente la latencia percibida (la primera vez pide
 * permiso, las siguientes ya está escuchando).
 *
 * El motor polifónico abre/cierra el micrófono por captura, ya que Basic
 * Pitch necesita una ventana de audio completa para inferir.
 */
export function useAudioEngine() {
  const {
    state: polyState,
    loadModel,
    analyzeFromMicrophone,
    analyzeSamples: analyzeSamplesPoly,
    stopRecording: stopPolyRecording,
  } = usePolyPitchDetector();

  const {
    state: monoState,
    start: startMono,
    stop: stopMono,
    subscribe: subscribeMono,
    detectNote: detectNoteMono,
    cancelPendingRequests: cancelMonoRequests,
    resetPitchHistory,
    getRecentSamples,
    waitForSilence: waitForSilenceMono,
    configureCapture: configureMonoCapture,
  } = useMonoPitchDetector();

  const initCalledRef = useRef(false);

  /**
   * Inicializa el motor: precarga el modelo polifónico en background.
   * NO pide permiso de micrófono — eso se difiere al primer detectNote
   * o detectChord, igual que en la versión anterior. Idempotente.
   */
  const init = useCallback(async () => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;
    if (!polyState.isReady && !polyState.isLoading) {
      void loadModel();
    }
  }, [loadModel, polyState.isReady, polyState.isLoading]);

  /**
   * Pide permiso de micrófono y arranca el motor monofónico en streaming.
   * Llamar cuando el usuario explícitamente quiere empezar a escuchar
   * (p.ej. al darle "Play" en práctica). Si no se llama, la primera
   * detectNote() lo invocará igual.
   */
  const requestMic = useCallback(async () => {
    try {
      await startMono();
    } catch {
      /* error queda en monoState.error */
    }
  }, [startMono]);

  /**
   * Detección de nota individual.
   *
   * @param timeoutMs — Tiempo máximo de espera. Default 2000.
   * @param opts.preferMidi — Si conoces la nota esperada, pásala: mejora
   *                          drásticamente la robustez (octavas, ruido).
   */
  const detectNote = useCallback(
    async (
      timeoutMs: number = 2000,
      opts?: DetectNoteAdvancedOptions
    ): Promise<DetectedNote | null> => {
      const detected = await detectNoteMono({
        targetMidi: opts?.preferMidi,
        acceptOctaveErrors: opts?.acceptOctaveErrors ?? false,
        acceptSemitoneErrors: opts?.acceptSemitoneErrors ?? false,
        timeoutMs,
        holdTimeMs: opts?.holdTimeMs ?? 150,
        minConfidence: opts?.minConfidence ?? 0.5,
        requireSilenceBeforeMs: opts?.requireSilenceBeforeMs ?? 0,
        maxCents: opts?.maxCents ?? 45,
      });
      if (!detected) return null;
      return {
        note: detected.note,
        octave: detected.octave,
        pitch: detected.midi,
        confidence: detected.confidence,
        frequency: detected.frequency,
        cents: detected.cents,
      };
    },
    [detectNoteMono]
  );

  /**
   * Detección de acordes (polifónica) con Basic Pitch.
   * Graba `durationMs` ms y analiza. Adecuada para captar combinaciones
   * de notas simultáneas (rasgueos, voicings).
   */
  const detectChord = useCallback(
    async (durationMs: number = 2500): Promise<DetectedChord | null> => {
      const events = await analyzeFromMicrophone(durationMs);
      if (events.length === 0) return null;

      const pitchClasses = Array.from(
        new Set(events.map((n) => Math.round(n.pitch) % 12))
      ).sort((a, b) => a - b);

      return { notes: events, pitchClasses };
    },
    [analyzeFromMicrophone]
  );

  /**
   * Basic Pitch sobre el buffer reciente del mic mono (sin segundo stream).
   * Filtra y asigna notas según el pentagrama (acordes simultáneos).
   */
  const detectExpectedPolyphony = useCallback(
    async (opts: {
      expectedMidis: number[];
      noteIndices: number[];
      captureMs?: number;
    }): Promise<GuidedPolyMatchResult> => {
      const empty: GuidedPolyMatchResult = {
        heardMidis: [],
        matchedNoteIndices: [],
        matchedExpectedMidis: [],
        coverage: 0,
      };
      if (opts.expectedMidis.length < 2) return empty;

      if (!polyState.isReady && !polyState.isLoading) {
        await loadModel();
      }

      try {
        await startMono();
      } catch {
        return empty;
      }

      const captureMs = opts.captureMs ?? 380;
      const { samples, sampleRate } = getRecentSamples(captureMs);
      if (samples.length < 512) return empty;

      const polyEvents = await analyzeSamplesPoly(samples, sampleRate, true);
      return matchGuidedPolyphony(polyEvents, opts.expectedMidis, opts.noteIndices);
    },
    [
      analyzeSamplesPoly,
      getRecentSamples,
      loadModel,
      polyState.isLoading,
      polyState.isReady,
      startMono,
    ]
  );

  /**
   * Cancela cualquier captura/detección en curso.
   * – Aborta la grabación polifónica si está activa.
   * – Resuelve a null las promesas pendientes de detectNote.
   *
   * NO cierra el micrófono monofónico (que permanece escuchando para
   * la siguiente nota). Usa `stop()` para cerrar todo.
   */
  const stopRecording = useCallback(() => {
    stopPolyRecording();
    cancelMonoRequests();
  }, [stopPolyRecording, cancelMonoRequests]);

  /** Tras acertar una nota: cancela detecciones viejas y limpia historia. */
  const prepareForNextNote = useCallback(() => {
    cancelMonoRequests();
    resetPitchHistory();
  }, [cancelMonoRequests, resetPitchHistory]);

  const waitForSilence = useCallback(
    (opts?: { minMs?: number; timeoutMs?: number }) =>
      waitForSilenceMono(opts),
    [waitForSilenceMono]
  );

  const subscribeToNotes = useCallback(
    (opts: SubscribeOptions): (() => void) => {
      void startMono();
      return subscribeMono(opts);
    },
    [startMono, subscribeMono]
  );

  /**
   * Cierra TODO el motor de audio (mono + poly). Llamar al desmontar o
   * al salir del flujo de detección por largo tiempo.
   */
  const shutdown = useCallback(() => {
    stopRecording();
    stopMono();
    initCalledRef.current = false;
  }, [stopRecording, stopMono]);

  // Limpieza por seguridad
  useEffect(() => {
    return () => {
      stopRecording();
      // stopMono ya se ejecuta en el cleanup del propio useMonoPitchDetector
    };
  }, [stopRecording]);

  // `isReady` mantiene la semántica anterior: indica que Basic Pitch
  // está cargado (sin esto, detectChord no funciona). detectNote (mono)
  // funciona independientemente, abre el mic la primera vez que se llama.
  const engineState: AudioEngineState = {
    isReady: polyState.isReady,
    isLoading: monoState.isLoading || polyState.isLoading,
    isAnalyzing: polyState.isAnalyzing,
    error: monoState.error || polyState.error,
    // El volumen del mono es en vivo (frame a frame). El poly solo lo
    // reporta tras una captura, así que el mono es más informativo
    // cuando el mic mono está activo.
    volume: monoState.isListening ? monoState.volume : polyState.volume,
  };

  return {
    state: engineState,
    init,
    requestMic,
    detectNote,
    detectChord,
    detectExpectedPolyphony,
    stopRecording,
    prepareForNextNote,
    resetPitchHistory,
    waitForSilence,
    subscribeToNotes,
    shutdown,
    configureCapture: configureMonoCapture,
    liveMonoState: monoState,
  };
}

export type { MonoDetectedNote };
